import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { ErrorBox } from '~/app/components/common/Error/ErrorBox';
import { Loader } from '~/app/components/common/Loader/Loader';
import { useAuth } from '~/app/auth/context/AuthContext';
import { OperationsKpiService } from '~/app/services/operations-kpi/operations-kpi.service';
import type { DashboardPeriod } from '~/app/services/organization-manager-dashboard/organization-manager-dashboard.types';
import {
  mapOperationsAccountability,
  mapOperationsRepairHours,
} from '../../map-operations-kpis';
import { isInitialDataLoad } from '../../utils';
import { OperatorAccountabilityCard } from '../OperatorAccountabilityCard/OperatorAccountabilityCard';
import { QuarterlyRepairHoursCard } from '../QuarterlyRepairHoursCard/QuarterlyRepairHoursCard';

export type OperationsKpisSectionProps = {
  period: DashboardPeriod;
  errorTitle: string;
  accountabilityTitle: string;
  accountabilitySubtitle: string;
  viewDetailsLabel: string;
  onViewDetails: (sectionId: string) => void;
};

export function OperationsKpisSection({
  period,
  errorTitle,
  accountabilityTitle,
  accountabilitySubtitle,
  viewDetailsLabel,
  onViewDetails,
}: OperationsKpisSectionProps) {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';

  const query = useQuery({
    queryKey: ['operations-kpis', organizationId, period],
    queryFn: () => OperationsKpiService.getKpis(period),
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
  });

  const accountability = useMemo(
    () => (query.data ? mapOperationsAccountability(query.data, t) : null),
    [query.data, t],
  );

  const repair = useMemo(
    () => (query.data ? mapOperationsRepairHours(query.data, t) : null),
    [query.data, t],
  );

  const showInitialLoader = isInitialDataLoad(query.isPending, query.data);

  if (showInitialLoader) {
    return (
      <Row gutter={[16, 16]} align="stretch">
        <Col span={24}>
          <Loader />
        </Col>
      </Row>
    );
  }

  if (query.isError) {
    const message =
      query.error instanceof Error
        ? query.error.message
        : t('errors.loadFallback');
    return (
      <Row gutter={[16, 16]} align="stretch">
        <Col span={24}>
          <ErrorBox title={errorTitle} description={message} />
        </Col>
      </Row>
    );
  }

  const isRefreshing = query.isFetching && query.isPlaceholderData;

  return (
    <Row
      gutter={[16, 16]}
      align="stretch"
      className={isRefreshing ? 'om-dashboard-section--refreshing' : undefined}
    >
      {accountability ? (
        <Col xs={24} xl={14} style={{ display: 'flex' }}>
          <div style={{ width: '100%' }}>
            <OperatorAccountabilityCard
              title={accountabilityTitle}
              subtitle={accountabilitySubtitle}
              accountability={accountability}
              viewDetailsLabel={viewDetailsLabel}
              onViewDetails={() => onViewDetails('accountability')}
            />
          </div>
        </Col>
      ) : null}
      {repair ? (
        <Col xs={24} xl={10} style={{ display: 'flex' }}>
          <div style={{ width: '100%' }}>
            <QuarterlyRepairHoursCard repair={repair} />
          </div>
        </Col>
      ) : null}
    </Row>
  );
}
